import React, { useState } from 'react';
import { Button, Typography, Avatar, Select, Drawer } from 'antd';
import { 
  UserOutlined,
  DashboardOutlined,
  TeamOutlined,
  TagsOutlined,
  UserAddOutlined,
  FundOutlined,
  CrownOutlined,
  LogoutOutlined,
  MenuOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import PanelLegalLinks from '../../../components/Common/PanelLegalLinks';
import { useNavigate } from 'react-router-dom';
import DashboardTab from './DashboardTab';
import MenteeListTab from './MenteeListTab';
import MentorListTab from './MentorListTab';
import CategoryListTab from './CategoryListTab';
import CompanyListTab from './CompanyListTab';
import SettingsTab from './SettingsTab';
import AdminAdvertisementTab from './AdminAdvertisementTab';
import AdminSubscriptionTab from './AdminSubscriptionTab';
import styles from './AdminPanel.module.css';

const { Title } = Typography;

const AdminPanel: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const menuItems = [
    { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
    { key: 'mentees', label: 'Mentee Listesi', icon: <UserOutlined /> },
    { key: 'mentors', label: 'Mentor Listesi', icon: <UserAddOutlined /> },
    { key: 'companies', label: 'Firma Talepleri', icon: <TeamOutlined /> },
    { key: 'categories', label: 'Kategori Listesi', icon: <TagsOutlined /> },
    { key: 'subscriptions', label: 'Abonelik Yönetimi', icon: <CrownOutlined /> },
    { key: 'advertisements', label: 'Reklam Yönetimi', icon: <FundOutlined /> }
  ];

  const handleMenuClick = (key: string) => {
    setSelectedMenu(key);
    setMobileMenuVisible(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const renderSidebar = () => (
    <>
      <div className="panel-sidebar-brand">
        <Avatar size={48} icon={<UserOutlined />} style={{ backgroundColor: 'var(--panel-accent)' }} />
        <h2 className="panel-user-name" style={{ marginTop: 12, marginBottom: 2 }}>{user?.name || 'Admin'}</h2>
        <span className="panel-user-role">Admin Paneli</span>
        {user?.email && <span className="panel-user-email">{user.email}</span>}
      </div>
      <nav className="panel-nav">
        {menuItems.map(item => (
          <button
            type="button"
            key={item.key}
            className={`panel-nav-item ${selectedMenu === item.key ? 'panel-nav-item--active' : ''}`}
            onClick={() => handleMenuClick(item.key)}
          >
            <span className="panel-nav-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="panel-sidebar-footer">
        <PanelLegalLinks />
        <button type="button" className="panel-logout" onClick={handleLogout}>
          <LogoutOutlined /> Çıkış Yap
        </button>
      </div>
    </>
  );

  const renderContent = () => {
    switch (selectedMenu) {
      case 'dashboard':
        return <DashboardTab />;
      case 'mentees':
        return <MenteeListTab />;
      case 'mentors':
        return <MentorListTab />;
      case 'companies':
        return <CompanyListTab />;
      case 'categories':
        return <CategoryListTab />;
      case 'subscriptions':
        return <AdminSubscriptionTab />;
      case 'advertisements':
        return <AdminAdvertisementTab />;
      case 'settings':
        return <SettingsTab />;
      default:
        return <DashboardTab />;
    }
  };

  return (
    <div className={styles.container} style={{ '--panel-accent': '#dc2626' } as React.CSSProperties}>
      <aside className="panel-sidebar">
        {renderSidebar()}
      </aside>

      <Drawer
        title="Menü"
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={260}
        className={styles.mobileDrawer}
        styles={{ header: { borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#f8fafc', background: '#1e293b' }, body: { padding: 0, background: '#1e293b' } }}
      >
        <div className="panel-sidebar" style={{ position: 'relative', width: '100%', boxShadow: 'none' }}>
          {renderSidebar()}
        </div>
      </Drawer>

      <div className="panel-content">
        <header className="panel-header">
          <div className="panel-header-left">
            <Button type="primary" icon={<MenuOutlined />} onClick={() => setMobileMenuVisible(true)} className={styles.mobileMenuButton} aria-label="Menüyü aç">
              <span className={styles.mobileMenuButtonText}>Menü</span>
            </Button>
            <Button type="text" icon={<HomeOutlined />} onClick={() => navigate('/')} className={styles.homeButton}>
              Anasayfa
            </Button>
          </div>
          <div className="panel-header-right">
            <span className={styles.welcomeText}>Hoş geldiniz, {user?.name || 'Admin'}</span>
          </div>
        </header>
        <main className="panel-main">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
